import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/AdminLayout';
import adminApi from '@/lib/adminApi';
import { formatApiError } from '@/lib/AuthContext';
import { Search, Plus, ChevronLeft, ChevronRight, Shield } from 'lucide-react';

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [createForm, setCreateForm] = useState({ email: '', name: '', password: '', role: 'member', organisation_id: '', persona: 'importer' });
  const [createError, setCreateError] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.getUsers({ page, search, role: roleFilter, status: statusFilter });
      setUsers(data.items);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) { console.error('Failed to fetch users:', err); }
    setLoading(false);
  }, [page, search, roleFilter, statusFilter]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = async () => {
    try {
      const { data } = await adminApi.getOrganisations({ per_page: 100 });
      setOrgs(data.items);
    } catch (err) { console.error('Failed to fetch organisations:', err); }
    setShowCreate(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    try {
      await adminApi.createUser(createForm);
      setShowCreate(false);
      setCreateForm({ email: '', name: '', password: '', role: 'member', organisation_id: '', persona: 'importer' });
      fetchUsers();
    } catch (err) {
      setCreateError(formatApiError(err.response?.data?.detail));
    }
  };

  const statusBadge = (s) => {
    const c = { active: 'bg-green-100 text-green-700', disabled: 'bg-yellow-100 text-yellow-700', deleted: 'bg-red-100 text-red-700' };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c[s] || 'bg-gray-100 text-gray-600'}`}>{s}</span>;
  };

  return (
    <AdminLayout title="Users" breadcrumbs={[{ label: 'Users' }]}>
      <div className="flex items-center gap-3 mb-4" data-testid="admin-users-toolbar">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#B0BEC5]" />
          <input data-testid="admin-users-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-9 pr-4 py-2 border border-[#E0E0E0] rounded-lg text-sm focus:border-[#0097A7] outline-none" placeholder="Search by name or email..." />
        </div>
        <select data-testid="admin-users-role-filter" value={roleFilter} onChange={e => { setRoleFilter(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]">
          <option value="">All roles</option>
          <option value="owner">Owner</option>
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
        <select data-testid="admin-users-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm text-[#4E6974]">
          <option value="">Active users</option>
          <option value="active">Active</option>
          <option value="disabled">Disabled</option>
          <option value="deleted">Deleted</option>
        </select>
        <button data-testid="admin-users-create-btn" onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-[#00707C] text-white text-sm rounded-lg hover:bg-[#005F6A] transition-colors">
          <Plus className="h-4 w-4" /> New user
        </button>
      </div>

      <div className="bg-white rounded-xl border border-[#E8ECF0] overflow-hidden" data-testid="admin-users-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E8ECF0] bg-[#F8FAFB]">
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Name</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Email</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Organisation</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Role</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">MFA</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-[#4E6974] uppercase">Last login</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-8 text-[#B0BEC5]">Loading...</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-[#B0BEC5]">No users found</td></tr>}
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer transition-colors"
                onClick={() => navigate(`/admin/users/${u.id}`)} data-testid={`admin-user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium text-[#0A2533] flex items-center gap-1.5">
                  {u.name} {u.is_platform_admin && <Shield className="h-3.5 w-3.5 text-[#0097A7]" />}
                </td>
                <td className="px-4 py-3 text-[#4E6974]">{u.email}</td>
                <td className="px-4 py-3 text-[#4E6974]">{u.organisation_name || '-'}</td>
                <td className="px-4 py-3 text-[#4E6974] capitalize">{u.role}</td>
                <td className="px-4 py-3">{statusBadge(u.status)}</td>
                <td className="px-4 py-3">{u.mfa_enabled ? <span className="text-xs text-green-600">Enabled</span> : <span className="text-xs text-[#B0BEC5]">Off</span>}</td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8ECF0]">
            <span className="text-xs text-[#4E6974]">{total} users</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
              <span className="text-xs text-[#4E6974]">{page} / {pages}</span>
              <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1 rounded hover:bg-[#F5F5F5] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="admin-create-user-modal">
            <h2 className="text-lg font-bold text-[#0A2533] mb-4">Create user</h2>
            {createError && <div className="text-sm text-red-600 bg-red-50 rounded-lg p-2 mb-3">{createError}</div>}
            <form onSubmit={handleCreate} className="space-y-3">
              <input data-testid="admin-create-user-name" value={createForm.name} onChange={e => setCreateForm(f => ({...f, name: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Full name" required />
              <input data-testid="admin-create-user-email" type="email" value={createForm.email} onChange={e => setCreateForm(f => ({...f, email: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Email" required />
              <input data-testid="admin-create-user-password" type="password" value={createForm.password} onChange={e => setCreateForm(f => ({...f, password: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm focus:border-[#0097A7] outline-none" placeholder="Password" required />
              <select data-testid="admin-create-user-org" value={createForm.organisation_id} onChange={e => setCreateForm(f => ({...f, organisation_id: e.target.value}))}
                className="w-full border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm" required>
                <option value="">Select organisation</option>
                {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="admin-create-user-role" value={createForm.role} onChange={e => setCreateForm(f => ({...f, role: e.target.value}))}
                  className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm">
                  <option value="member">Member</option><option value="admin">Admin</option><option value="owner">Owner</option>
                </select>
                <select data-testid="admin-create-user-persona" value={createForm.persona} onChange={e => setCreateForm(f => ({...f, persona: e.target.value}))}
                  className="border border-[#E0E0E0] rounded-lg px-3 py-2 text-sm">
                  <option value="importer">Importer</option><option value="government">Government</option><option value="defence">Defence</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="flex-1 py-2 border border-[#E0E0E0] rounded-lg text-sm text-[#4E6974] hover:bg-[#F5F5F5]">Cancel</button>
                <button type="submit" data-testid="admin-create-user-submit" className="flex-1 py-2 bg-[#00707C] text-white rounded-lg text-sm hover:bg-[#005F6A]">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
